import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  EmptyState,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  Text,
  type EntityPoolConfig,
  type EntitySelectorConfig,
  type EntitySelection,
  type Omittable,
} from "@salestrak/ui";
import { iconSourceToName } from "#app/lib/entity-visuals";
import { SelectionEntityTrigger } from "#app/components/entity-selectors/selection-entity-trigger";
import { useDraftedSelection } from "#app/components/entity-selectors/use-drafted-selection";
import { selectionToTeamEntity } from "#app/components/entity-selectors/utils";
import {
  TEAM_POOL_ID,
  teamToPoolItem,
  useTeamPool,
} from "#app/components/entity-selectors/hooks/use-team-pool";
import { CreateTeamDialog } from "#app/components/teams/create-team-dialog";
import {
  DETAIL_EDIT,
  useIssueViewMutations,
} from "#app/hooks/issue-view/use-issue-view-mutations";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useMentionAccessActions } from "#app/lib/mention-access-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import type { IssueRow } from "#app/lib/data";

// The picker's create-mode "Suggested" section — the creator's recently used
// teams pinned above the pool. Suggestion lives in the picker, never inline in
// the property row: the 32px row is a value slot and stays single-line in
// every state.
const SUGGESTED_POOL_ID = "suggested-teams";
const MAX_SUGGESTED = 2;
const MAX_REMEMBERED = 3;

type IssueTeamsPropertyProps = Omittable<{
  /** Absent in create mode (the draft owns the value). */
  issue?: IssueRow;
  /** False for view/comment-tier viewers — static value, picker can't open. */
  editable: boolean;
  /** Viewer access — drives the grant-access prompt's share actions. */
  viewerCanManageSharing?: boolean;
}>;

// The Teams property row value: team-level responsibility, parallel to
// Assignees. Same trigger grammar (ghost field button; single team = tile +
// name, several = square-tile stack + square +N, hover clear-all) with the
// team pool swapped in. Mirrors the assignees row's draft + debounced
// auto-save shape, minus the primary concept.
export function IssueTeamsProperty({
  issue,
  editable,
  viewerCanManageSharing = false,
}: IssueTeamsPropertyProps) {
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const { updateTeams } = useIssueViewMutations(DETAIL_EDIT);
  const { promptIfMissing } = useMentionAccessActions();
  const { pool, loading: poolLoading, hasAny: hasAnyTeams } = useTeamPool();
  const canCreateTeam = usePermission("manage.teams");

  // Picker open state is controlled so the create-team flow can close it
  // before the dialog opens (a modal dialog under a still-open popover
  // fights it for focus).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Team created from the picker's empty state — auto-selected once the
  // refetched pool delivers it (createTeam refetches TeamsListData).
  const pendingCreatedTeamIdRef = useRef<string | null>(null);

  // Recently used teams (workspace teams only survive the pool intersection
  // below, so a stale id from another workspace can never surface).
  const { state: recent, update: updateRecent } = usePersistedState<{
    ids: string[];
  }>("recent-teams", { ids: [] });

  const rememberTeams = useCallback(
    (teamIds: string[]) => {
      if (!teamIds.length) return;
      updateRecent({
        ids: [
          ...teamIds,
          ...recent.ids.filter((id) => !teamIds.includes(id)),
        ].slice(0, MAX_REMEMBERED),
      });
    },
    [recent.ids, updateRecent],
  );

  const initialSelections = useMemo<EntitySelection[]>(() => {
    if (isCreateMode) return createCtx.draft.teams;
    return (issue?.teams ?? []).map((team) => ({
      id: team.teamId,
      poolId: TEAM_POOL_ID,
      entity: teamToPoolItem({
        teamId: team.teamId,
        teamName: team.teamName,
        icon: team.icon,
      }),
    }));
  }, [isCreateMode, createCtx, issue?.teams]);

  const flushMutation = useCallback(
    (selections: EntitySelection[]) => {
      if (!issue) return;
      updateTeams({
        variables: {
          issueId: issue.issueId,
          teamIds: selections.map((s) => s.id),
        },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            teams: selections.map((s) => ({
              __typename: "Team" as const,
              id: s.id,
              teamId: s.id,
              teamName: s.entity.title,
              icon: {
                __typename: "Icon" as const,
                // avatar.icon is an IconSource (name or component); the schema
                // Icon carries only the name.
                icon: iconSourceToName(s.entity.avatar?.icon),
                color: s.entity.avatar?.color ?? null,
                percentage: null,
              },
            })),
          },
        },
        onError: () => {
          // The optimistic layer already rolled back; the global error link
          // toasts FORBIDDEN, so nothing further to surface here.
        },
      });
    },
    [issue, updateTeams],
  );

  const onClosedWithAdditions = useCallback(
    (addedTeamIds: string[]) => {
      // The assignment already applied (debounced separately); the prompt
      // never blocks it.
      if (!issue) return;
      promptIfMissing({
        issueId: issue.issueId,
        userIds: [],
        teamIds: addedTeamIds,
        viewerCanManageSharing,
        variant: "assignment",
      });
    },
    [issue, promptIfMissing, viewerCanManageSharing],
  );

  const createDraft = useMemo(
    () =>
      isCreateMode
        ? (selections: EntitySelection[]) => createCtx.updateDraft({ teams: selections })
        : undefined,
    [isCreateMode, createCtx],
  );

  const {
    draftSelections,
    draftRef,
    setDraft,
    handleOpenChange: setDraftOpen,
  } = useDraftedSelection({
    initialSelections,
    flush: flushMutation,
    draft: createDraft,
    onClosedWithAdditions,
  });


  const selectedIds = useMemo(
    () => new Set(draftSelections.map((s) => s.id)),
    [draftSelections],
  );

  // Create mode only — an existing project's ownership is a decision, not a
  // guess, so the view-mode picker opens straight into the pool.
  const suggestedPool = useMemo<EntityPoolConfig | null>(() => {
    if (!isCreateMode) return null;
    const byId = new Map(pool.items.map((item) => [item.id, item]));
    const items = recent.ids
      .map((id) => byId.get(id))
      .filter((item): item is NonNullable<typeof item> => !!item)
      .filter((item) => !selectedIds.has(item.id))
      .slice(0, MAX_SUGGESTED)
      .map((item) => ({ ...item, description: "Recently used" }));
    if (!items.length) return null;
    return {
      id: SUGGESTED_POOL_ID,
      label: "Suggested",
      items,
      // The team pool's own "Team" badge (navigate to the team page, close
      // the picker first) — the suggested rows are the same teams.
      itemBadge: pool.itemBadge,
    };
  }, [isCreateMode, pool.items, pool.itemBadge, recent.ids, selectedIds]);

  // Suggested teams render in their own pool — drop them from the main pool
  // so no team lists twice. Applied teams STAY in the pool: the mounted
  // Selected section omits them itself and counts them as listed above, which
  // is what keeps "No teams found" from painting under a list whose every
  // team is chosen. Filtering them here defeated that guard.
  const mainPool = useMemo<EntityPoolConfig>(() => {
    const suggestedIds = new Set(
      suggestedPool?.items.map((item) => item.id) ?? [],
    );
    return {
      ...pool,
      items: pool.items.filter((item) => !suggestedIds.has(item.id)),
    };
  }, [pool, suggestedPool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      const prevIds = new Set(draftRef.current.map((s) => s.id));
      rememberTeams(next.map((s) => s.id).filter((id) => !prevIds.has(id)));
      setDraft(next);
    },
    [draftRef, setDraft, rememberTeams],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setPickerOpen(nextOpen);
      setDraftOpen(nextOpen);
    },
    [setDraftOpen],
  );

  // Auto-select a team created from the picker's empty state as soon as the
  // refetched pool carries it — the row fills in without reopening anything.
  useEffect(() => {
    const pending = pendingCreatedTeamIdRef.current;
    if (!pending) return;
    const item = pool.items.find((i) => i.id === pending);
    if (!item) return;
    pendingCreatedTeamIdRef.current = null;
    handleValueChange([
      ...draftRef.current,
      { id: item.id, poolId: TEAM_POOL_ID, entity: item },
    ]);
  }, [pool.items, handleValueChange, draftRef]);

  const handleCreateTeamPress = useCallback(() => {
    handleOpenChange(false);
    setCreateOpen(true);
  }, [handleOpenChange]);

  const handleTeamCreated = useCallback((teamId: string) => {
    pendingCreatedTeamIdRef.current = teamId;
  }, []);

  // Workspace genuinely has no teams (the base list, not a search miss) —
  // the picker becomes an empty state: a create action for viewers with
  // manage.teams, a muted explainer for everyone else.
  const showNoTeams = !poolLoading && !hasAnyTeams;

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: suggestedPool ? [suggestedPool, mainPool] : [mainPool],
      searchPlaceholder: "Search teams...",
      emptyMessage: "No teams found",
    }),
    [suggestedPool, mainPool],
  );

  return (
    <>
      <EntitySelector
        title="Teams"
        config={config}
        value={draftSelections}
        onValueChange={handleValueChange}
        open={pickerOpen}
        onOpenChange={handleOpenChange}
      >
        <SelectionEntityTrigger
          toEntity={selectionToTeamEntity}
          placeholder="Add Teams..."
          title="Teams"
          emptyTextVariant="subtle"
          size="default"
          readOnly={!editable}
          // Hover-revealed × on the row's far right clears every team.
          onClear={editable ? () => handleValueChange([]) : undefined}
        />
        <EntitySelectorContent>
          {showNoTeams ? (
            // The sm rung fits the popover — the page-scale paddings this
            // used to override with a className are the rung's own metrics.
            <EmptyState
              size="sm"
              media="icon"
              icon="users"
              title="No teams yet"
              description={
                canCreateTeam
                  ? "Create and assign a team."
                  : "Ask an admin to create teams."
              }
              actions={
                canCreateTeam ? (
                  <Button size="sm" onClick={handleCreateTeamPress}>
                    <Text>Create Team</Text>
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <EntitySelectorInput />
              {/* The hover "Team" row action rides the pool's own badge
                * (useTeamOpenBadge, baked in by useTeamPool) — no per-list
                * handler wiring. */}
              <EntitySelectorSelected
                label="Teams"
                hideWhenEmpty
                hideBadge
                hideSelectionHighlight
              />
              {suggestedPool ? (
                <EntitySelectorPool
                  value={SUGGESTED_POOL_ID}
                  hideSelectionHighlight
                />
              ) : null}
              <EntitySelectorPool
                value={TEAM_POOL_ID}
                label="All Teams"
                hideSelectionHighlight
              />
              <EntitySelectorEmpty>No teams found</EntitySelectorEmpty>
            </>
          )}
        </EntitySelectorContent>
      </EntitySelector>
      {canCreateTeam ? (
        <CreateTeamDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={handleTeamCreated}
        />
      ) : null}
    </>
  );
}
